import { randomString } from '@/lib/random'
import { cn } from '@/lib/utils'
import { useAtomValue } from 'jotai'
import { autoLoadMediaAtom } from '@/atoms/preferences'
import modalManager from '@/services/modal-manager.service'
import { TImetaInfo } from '@/types'
import { ReactNode, useEffect, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import Lightbox from 'yet-another-react-lightbox'
import Zoom from 'yet-another-react-lightbox/plugins/zoom'
import Image from './Image'
import ImageWithLightbox from './ImageWithLightbox'

export default function ImageGallery({
  className,
  images,
  start = 0,
  end = images.length,
  mustLoad = false
}: {
  className?: string
  images: (string | TImetaInfo)[]
  start?: number
  end?: number
  mustLoad?: boolean
}) {
  const id = useMemo(() => `image-gallery-${randomString()}`, [])
  const autoLoadMedia = useAtomValue(autoLoadMediaAtom)
  const [index, setIndex] = useState(-1)
  const slides = useMemo(
    () => images.map((imeta) => ({ src: typeof imeta === 'string' ? imeta : imeta.url })),
    [images]
  )
  const plugins = useMemo(() => [Zoom], [])
  const controller = useMemo(
    () => ({
      closeOnBackdropClick: true,
      closeOnPullUp: true,
      closeOnPullDown: true
    }),
    []
  )
  const carouselOptions = useMemo(
    () => ({
      finite: images.length <= 1
    }),
    [images.length]
  )
  const renderOptions = useMemo(
    () => ({
      buttonPrev: images.length <= 1 ? () => null : undefined,
      buttonNext: images.length <= 1 ? () => null : undefined
    }),
    [images.length]
  )
  const toolbarStyles = useMemo(
    () => ({
      toolbar: { paddingTop: '2.25rem' }
    }),
    []
  )
  useEffect(() => {
    if (index >= 0) {
      modalManager.register(id, () => {
        setIndex(-1)
      })
    } else {
      modalManager.unregister(id)
    }
  }, [index])

  const displayImages = images.slice(start, end)

  if (!mustLoad && !autoLoadMedia) {
    return displayImages.map((image, i) => (
      <ImageWithLightbox
        key={i}
        image={image}
        className="max-h-[80vh] sm:max-h-[50vh] object-contain"
        classNames={{
          wrapper: cn('w-fit max-w-full', className)
        }}
      />
    ))
  }

  let imageContent: ReactNode | null = null
  if (displayImages.length === 1) {
    const first = displayImages[0]
    const [url, pubkey, imeta] =
      typeof first === 'string' ? [first, undefined, undefined] : [first.url, first.pubkey, first]

    imageContent = (
      <Image
        imageUrl={url}
        imagePubkey={pubkey}
        imeta={imeta}
        className="max-h-[80vh] sm:max-h-[50vh] object-contain"
        classNames={{
          placeholder: 'aspect-square h-[30vh]',
          wrapper: 'cursor-zoom-in'
        }}
        imgproxyModifiers="rs:fit:800:800"
        onClick={(e) => handlePhotoClick(e, 0)}
      />
    )
  } else if (displayImages.length === 2 || displayImages.length === 4) {
    imageContent = (
      <div className="grid grid-cols-2 gap-2 w-full">
        {displayImages.map((image, i) => {
          const [url, pubkey, imeta] =
            typeof image === 'string'
              ? [image, undefined, undefined]
              : [image.url, image.pubkey, image]

          return (
            <Image
              imageUrl={url}
              imagePubkey={pubkey}
              imeta={imeta}
              key={i}
              className="aspect-square w-full"
              classNames={{ wrapper: 'cursor-zoom-in' }}
              imgproxyModifiers="rs:fit:400:400"
              onClick={(e) => handlePhotoClick(e, i)}
            />
          )
        })}
      </div>
    )
  } else {
    imageContent = (
      <div className="grid grid-cols-3 gap-2 w-full">
        {displayImages.map((image, i) => {
          const [url, pubkey, imeta] =
            typeof image === 'string'
              ? [image, undefined, undefined]
              : [image.url, image.pubkey, image]

          return (
            <Image
              imageUrl={url}
              imagePubkey={pubkey}
              imeta={imeta}
              key={i}
              className="aspect-square w-full"
              classNames={{ wrapper: 'cursor-zoom-in' }}
              imgproxyModifiers="rs:fit:300:300"
              onClick={(e) => handlePhotoClick(e, i)}
            />
          )
        })}
      </div>
    )
  }

  return (
    <div className={cn(displayImages.length === 1 ? 'w-fit max-w-full' : 'w-full', className)}>
      {imageContent}
      {index >= 0 &&
        createPortal(
          <div onClick={(e) => e.stopPropagation()}>
            <Lightbox
              index={index}
              slides={slides}
              plugins={plugins}
              open={index >= 0}
              close={() => setIndex(-1)}
              controller={controller}
              carousel={carouselOptions}
              render={renderOptions}
              styles={toolbarStyles}
            />
          </div>,
          document.body
        )}
    </div>
  )

  function handlePhotoClick(event: React.MouseEvent, current: number) {
    event.stopPropagation()
    event.preventDefault()
    setIndex(start + current)
  }
}
